// hooks/useAuth.js

import { useState, useEffect, useRef } from "react";
import { auth, db } from "../network/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";

// Cache for user data
const userCache = {
    data: null,
    timestamp: null,
    expiryTime: 5 * 60 * 1000, // 5 minutes
    usersData: null,
    usersTimestamp: null,
    usersExpiryTime: 10 * 60 * 1000, // 10 minutes
};

// Check if cache is valid
const isCacheValid = (timestamp, expiryTime) => {
    return timestamp && (Date.now() - timestamp) < expiryTime;
};

export const useAuth = () => {
    const [user, setUser] = useState(() => {
        // Initialize from cache if valid
        if (isCacheValid(userCache.timestamp, userCache.expiryTime)) {
            return userCache.data;
        }
        return null;
    });

    const [users, setUsers] = useState(() => {
        // Initialize from cache if valid
        if (isCacheValid(userCache.usersTimestamp, userCache.usersExpiryTime)) {
            return userCache.usersData;
        }
        return [];
    });

    const [loading, setLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(true);
    const authListenerRef = useRef(null);
    const usersFetchedRef = useRef(false);

    // Auth state listener - only one instance
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // Check cache first
                if (isCacheValid(userCache.timestamp, userCache.expiryTime) && userCache.data?.uid === currentUser.uid) {
                    setUser(userCache.data);
                    setLoading(false);
                    return;
                }

                try {
                    const userRef = doc(db, "users", currentUser.uid);
                    const employeeRef = doc(db, "employee", currentUser.uid);

                    const [userSnap, employeeSnap] = await Promise.all([
                        getDoc(userRef),
                        getDoc(employeeRef),
                    ]);

                    let userData;
                    if (userSnap.exists()) {
                        userData = { uid: currentUser.uid, ...userSnap.data() };
                    } else if (employeeSnap.exists()) {
                        userData = { uid: currentUser.uid, ...employeeSnap.data() };
                    } else {
                        userData = {
                            uid: currentUser.uid,
                            email: currentUser.email,
                            role: "unassigned",
                        };
                    }

                    // Update cache
                    userCache.data = userData;
                    userCache.timestamp = Date.now();

                    setUser(userData);
                } catch (err) {
                    console.error("Failed to fetch user info:", err);
                    const fallbackData = { uid: currentUser.uid, email: currentUser.email };
                    userCache.data = fallbackData;
                    userCache.timestamp = Date.now();
                    setUser(fallbackData);
                }
            } else {
                // Clear cache on logout
                userCache.data = null;
                userCache.timestamp = null;
                setUser(null);
            }
            setLoading(false);
        });

        authListenerRef.current = unsubscribe;
        return () => {
            if (authListenerRef.current) {
                authListenerRef.current();
            }
        };
    }, []);

    // Load all users with caching
    useEffect(() => {
        const loadAllUsers = async () => {
            // Check cache first
            if (isCacheValid(userCache.usersTimestamp, userCache.usersExpiryTime) && userCache.usersData) {
                setUsers(userCache.usersData);
                setUsersLoading(false);
                usersFetchedRef.current = true;
                return;
            }

            try {
                setUsersLoading(true);
                const usersSnap = await getDocs(collection(db, "users"));

                const allUsers = usersSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Update cache
                userCache.usersData = allUsers;
                userCache.usersTimestamp = Date.now();

                setUsers(allUsers);
            } catch (err) {
                console.error("Failed to load all users:", err);
                // If cache exists but expired, use it as fallback
                if (userCache.usersData) {
                    setUsers(userCache.usersData);
                }
            } finally {
                setUsersLoading(false);
                usersFetchedRef.current = true;
            }
        };

        loadAllUsers();
    }, []);

    // Force refresh user data
    const refreshUser = async () => {
        userCache.timestamp = null;
        userCache.data = null;
        setLoading(true);
        // Re-trigger auth state check
        if (auth.currentUser) {
            const user = auth.currentUser;
            try {
                const userRef = doc(db, "users", user.uid);
                const employeeRef = doc(db, "employee", user.uid);
                const [userSnap, employeeSnap] = await Promise.all([
                    getDoc(userRef),
                    getDoc(employeeRef),
                ]);

                let userData;
                if (userSnap.exists()) {
                    userData = { uid: user.uid, ...userSnap.data() };
                } else if (employeeSnap.exists()) {
                    userData = { uid: user.uid, ...employeeSnap.data() };
                } else {
                    userData = { uid: user.uid, email: user.email, role: "unassigned" };
                }

                userCache.data = userData;
                userCache.timestamp = Date.now();
                setUser(userData);
            } catch (err) {
                console.error("Failed to refresh user info:", err);
            }
        }
        setLoading(false);
    };

    // Force refresh users list
    const refreshUsers = async () => {
        userCache.usersTimestamp = null;
        userCache.usersData = null;
        setUsersLoading(true);

        try {
            const usersSnap = await getDocs(collection(db, "users"));
            const allUsers = usersSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            userCache.usersData = allUsers;
            userCache.usersTimestamp = Date.now();
            setUsers(allUsers);
        } catch (err) {
            console.error("Failed to refresh users:", err);
        } finally {
            setUsersLoading(false);
        }
    };

    return {
        user,
        users,
        loading,
        usersLoading,
        refreshUser,
        refreshUsers,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isTechnician: user?.role === 'technician',
        isHelpdesk: user?.role === 'helpdesk',
        isSupervisor: user?.role === 'supervisor',
        hasRole: (roles) => roles.includes(user?.role),
    };
};